/**
 * Offline Photo Storage
 *
 * Manages tracking of offline-uploaded photos for area objects (racks, switches, ODFs, etc.)
 * and premise outlets. Provides utilities to:
 * - Track blob URLs associated with entity IDs
 * - Get pending photos for an entity
 * - Clean up blob URLs after sync
 * - Subscribe to photo sync events
 */

import { isBlobUrl } from '../utils';
import { getPendingUploadsForEntity, getAllFileUploads, PendingFileUpload, recreateBlobUrl } from './fileStorage';
import { fileSyncManager } from './fileSyncManager';

/**
 * Map of entity key (entityType:entityId) to list of local blob URLs
 * This is used for display only - the actual files are in IndexedDB
 */
const localPhotoUrlsByEntity: Map<string, Set<string>> = new Map();

/**
 * Map of blob URL to upload ID for reverse lookup
 */
const uploadIdByBlobUrl: Map<string, string> = new Map();

/**
 * Subscribers for photo storage changes
 * Used to notify components when photos are added/removed
 */
type PhotoChangeCallback = (entityType: string, entityId: string) => void;
const photoChangeSubscribers: Set<PhotoChangeCallback> = new Set();

/**
 * Subscribe to photo storage changes
 * Returns an unsubscribe function
 */
export function subscribeToPhotoChanges(callback: PhotoChangeCallback): () => void {
  photoChangeSubscribers.add(callback);
  return () => {
    photoChangeSubscribers.delete(callback);
  };
}

/**
 * Notify subscribers of a photo change
 */
function notifyPhotoChange(entityType: string, entityId: string): void {
  photoChangeSubscribers.forEach((callback) => {
    try {
      callback(entityType, entityId);
    } catch {
      // Ignore errors in subscribers
    }
  });
}

/**
 * Create entity key from type and ID
 */
function getEntityKey(entityType: string, entityId: string): string {
  return `${entityType}:${entityId}`;
}

/**
 * Add a local blob URL for an entity
 */
export function addLocalPhoto(entityType: string, entityId: string, blobUrl: string, uploadId: string): void {
  const key = getEntityKey(entityType, entityId);
  if (!localPhotoUrlsByEntity.has(key)) {
    localPhotoUrlsByEntity.set(key, new Set());
  }
  localPhotoUrlsByEntity.get(key)!.add(blobUrl);
  uploadIdByBlobUrl.set(blobUrl, uploadId);

  // Notify subscribers of the change
  notifyPhotoChange(entityType, entityId);
}

/**
 * Remove a local blob URL for an entity
 */
export function removeLocalPhoto(entityType: string, entityId: string, blobUrl: string): void {
  const key = getEntityKey(entityType, entityId);
  const urls = localPhotoUrlsByEntity.get(key);
  if (urls) {
    urls.delete(blobUrl);
    if (urls.size === 0) {
      localPhotoUrlsByEntity.delete(key);
    }
  }
  uploadIdByBlobUrl.delete(blobUrl);

  // Revoke the blob URL to free memory
  try {
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Ignore errors revoking URL
  }

  // Notify subscribers of the change
  notifyPhotoChange(entityType, entityId);
}

/**
 * Get all local blob URLs for an entity
 */
export function getLocalPhotos(entityType: string, entityId: string): string[] {
  const key = getEntityKey(entityType, entityId);
  const urls = localPhotoUrlsByEntity.get(key);
  return urls ? Array.from(urls) : [];
}

/**
 * Get combined photo URLs (real + local blob URLs)
 * Filters out blob URLs from the real URLs array to prevent duplicates
 */
export function getCombinedPhotoUrls(realUrls: readonly string[], entityType: string, entityId: string): string[] {
  const filteredRealUrls = realUrls.filter((url) => !isBlobUrl(url));
  const localUrls = getLocalPhotos(entityType, entityId);
  return [...filteredRealUrls, ...localUrls];
}

/**
 * Clear all local photos for an entity
 * Called when the entity is refreshed from server with updated photos
 */
export function clearLocalPhotos(entityType: string, entityId: string): void {
  const key = getEntityKey(entityType, entityId);
  const urls = localPhotoUrlsByEntity.get(key);
  if (urls) {
    urls.forEach((blobUrl) => {
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {
        // Ignore errors
      }
      uploadIdByBlobUrl.delete(blobUrl);
    });
    localPhotoUrlsByEntity.delete(key);
  }
}

/**
 * Clear all local photos across all entities
 * Used when discarding all local changes
 */
export function clearAllLocalPhotos(): void {
  // Revoke all blob URLs and clear maps
  localPhotoUrlsByEntity.forEach((urls) => {
    urls.forEach((blobUrl) => {
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {
        // Ignore errors
      }
    });
  });

  localPhotoUrlsByEntity.clear();
  uploadIdByBlobUrl.clear();
}

/**
 * Get the upload ID associated with a blob URL
 */
export function getUploadIdForBlobUrl(blobUrl: string): string | undefined {
  return uploadIdByBlobUrl.get(blobUrl);
}

/**
 * Initialize local photo tracking from existing pending uploads
 * Call this on app startup to restore tracking state
 *
 * IMPORTANT: Blob URLs don't persist across page navigation/reload.
 * We must recreate them from the stored file data in IndexedDB.
 */
export async function initializeFromPendingUploads(): Promise<void> {
  try {
    const uploads = await getAllFileUploads();

    // Process pending uploads and recreate blob URLs
    const pendingUploads = uploads.filter((upload) => upload.status === 'pending');

    // Recreate blob URLs for all pending uploads
    const recreatePromises = pendingUploads.map(async (upload) => {
      // Recreate the blob URL from stored file data
      // The old localUrl is no longer valid after navigation
      const newBlobUrl = await recreateBlobUrl(upload.id);
      if (newBlobUrl) {
        addLocalPhoto(upload.entityType, upload.entityId, newBlobUrl, upload.id);
      }
    });

    await Promise.all(recreatePromises);
  } catch {
    // Ignore initialization errors
  }
}

/**
 * Subscribe to file sync events and clean up local photos when uploads complete
 */
export function subscribeToFileSyncEvents(): () => void {
  return fileSyncManager.subscribe((event) => {
    if (event.type === 'FILE_UPLOADED') {
      const { entityType, entityId } = event.payload;

      // Find and remove the local blob URL for this entity
      const localUrls = getLocalPhotos(entityType, entityId);
      if (localUrls.length > 0) {
        // Remove the first local URL (FIFO order)
        // This assumes uploads complete in the same order they were queued
        const blobUrl = localUrls[0];
        removeLocalPhoto(entityType, entityId, blobUrl);
      }
    }
  });
}

/**
 * Hook to get pending uploads for a specific entity
 * Useful for components that need to display pending upload indicators
 */
export async function getPendingPhotosForEntity(entityType: string, entityId: string): Promise<PendingFileUpload[]> {
  return getPendingUploadsForEntity(entityType, entityId);
}
